/**
 * PromptEditor Component
 * 
 * Modal-based prompt editor for real-time AI prompt customization.
 * Allows users to edit system prompts during pilot phase for quick optimization.
 * 
 * Features:
 * - Modal interface for editing prompts
 * - Real-time preview
 * - Save with instant application
 * - Version history tracking
 * - Reset to default option
 * 
 * @module components/PromptEditor
 */

import { h } from 'preact';
import AppState from '../core/AppState.js';
import EventBus, { AppEvents } from '../core/EventBus.js';
import PromptService from '../services/PromptService.js';
import StorageService from '../services/StorageService.js';
import { Toast } from '../utils/ui.js';
import { confirmOverlay } from '../utils/Overlay.js';
import { ModalService } from '../ui-kit/ModalService.jsx';
import promptVersionControl from '../services/PromptVersionControl.js';
import PromptEditorIsland from '../ui/prompt/PromptEditor.jsx';

export class PromptEditor {
  constructor() {
    this.currentPromptKey = null;
    this.originalPrompt = null;
    this.promptHistory = StorageService.get('promptHistory', {});
    this.isOpen = false;
  }
  
  /**
   * Open the prompt editor for a specific prompt
   * @param {string} promptKey - The key of the prompt to edit
   * @param {string} promptName - Human-readable name for the prompt
   */
  open(promptKey, promptName) {
    this.currentPromptKey = promptKey;
    this.originalPrompt = PromptService.getPrompt(promptKey);
    this.isOpen = true;
    
    if (!this.originalPrompt) {
      Toast.show(`Prompt '${promptName}' not found`, 'error');
      return;
    }
    
    // Mount the Preact island inside ModalService; local footer handles actions
    this._closeFn = ModalService.open({
      title: `🎯 Edit AI Prompt: ${promptName}`,
      content: h(PromptEditorIsland, { promptKey, promptName }),
      actions: []
    });
  }
  
  /**
   * Create the editor content HTML
   */
  createEditorContent(promptKey, promptName) {
    const stats = this.getPromptStats(promptKey);
    const currentVersion = promptVersionControl.getCurrentVersion(promptKey);
    const versionHistory = promptVersionControl.getHistory(promptKey, 5);
    const versionStats = promptVersionControl.getStatistics(promptKey);
    
    return `
      <div class="prompt-editor">
        <!-- Version Info Bar -->
        <div class="version-info-bar">
          <div class="version-badge">
            <span class="version-label">Version:</span>
            <span class="version-number">${currentVersion}</span>
          </div>
          <div class="version-stats">
            <span title="Total versions">📚 ${versionStats.totalVersions} versions</span>
            <span title="Major/Minor/Patch">🔄 ${versionStats.majorChanges}/${versionStats.minorChanges}/${versionStats.patches}</span>
            <span title="Last modified">🕰️ ${versionStats.lastModified ? new Date(versionStats.lastModified).toLocaleDateString() : 'Never'}</span>
          </div>
        </div>
        
        <!-- Header Info -->
        <div class="editor-info">
          <div class="info-item">
            <span class="info-label">Prompt ID:</span>
            <span class="info-value">${promptKey}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Last Author:</span>
            <span class="info-value">${versionStats.lastAuthor || 'System'}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Change Frequency:</span>
            <span class="info-value">${versionStats.changeFrequency}</span>
          </div>
        </div>
        
        <!-- Main Editor -->
        <div class="editor-section">
          <label for="prompt-editor-textarea">System Prompt:</label>
          <textarea 
            id="prompt-editor-textarea"
            class="prompt-textarea"
            rows="15"
            placeholder="Enter the system prompt..."
          ></textarea>
          <div class="char-count">
            <span id="char-count">0</span> characters
          </div>
        </div>
        
        <!-- Variables Section (if applicable) -->
        ${this.originalPrompt.variables ? `
          <div class="variables-section">
            <label>Available Variables:</label>
            <div class="variables-list">
              ${this.originalPrompt.variables.map(v => `
                <span class="variable-tag">{${v}}</span>
              `).join('')}
            </div>
            <p class="help-text">These variables will be replaced with actual values when the prompt is used.</p>
          </div>
        ` : ''}
        
        <!-- Temperature Settings -->
        <div class="temperature-section">
          <label for="temperature-slider">
            Temperature: <span id="temperature-value">${this.originalPrompt.temperature || 0.7}</span>
          </label>
          <input 
            type="range" 
            id="temperature-slider"
            min="0" 
            max="1" 
            step="0.1"
            value="${this.originalPrompt.temperature || 0.7}"
            class="temperature-slider"
          />
          <p class="help-text">Lower = More focused/deterministic | Higher = More creative/random</p>
        </div>
        
        <!-- History Section -->
        ${versionHistory && versionHistory.length > 0 ? `
          <div class="history-section">
            <label>Version History:</label>
            <div class="version-history-list">
              ${versionHistory.map((v, i) => `
                <div class="version-history-item">
                  <div class="version-item-header">
                    <span class="version-tag">v${v.version}</span>
                    <span class="version-date">${new Date(v.timestamp).toLocaleString()}</span>
                  </div>
                  <div class="version-item-details">
                    <span class="version-author">👤 ${v.author}</span>
                    <span class="version-change">${v.changeDescription}</span>
                    ${v.diff ? `<span class="version-diff">+${v.diff.added} -${v.diff.removed} ±${v.diff.modified}</span>` : ''}
                  </div>
                  <button class="btn-small" data-version="${v.version}" data-action="rollback">
                    Restore
                  </button>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}
        
        <!-- Change Type Selector -->
        <div class="change-type-section">
          <label>Change Type for This Edit:</label>
          <div class="change-type-selector">
            <input type="radio" id="change-patch" name="changeType" value="patch" checked>
            <label for="change-patch">
              <strong>Patch</strong> - Minor fixes, typos, small adjustments
            </label>
            
            <input type="radio" id="change-minor" name="changeType" value="minor">
            <label for="change-minor">
              <strong>Minor</strong> - New features, moderate changes
            </label>
            
            <input type="radio" id="change-major" name="changeType" value="major">
            <label for="change-major">
              <strong>Major</strong> - Breaking changes, complete rewrites
            </label>
          </div>
          
          <div class="change-description">
            <label for="change-description">Change Description:</label>
            <input type="text" id="change-description" placeholder="Brief description of changes..." />
          </div>
        </div>
        
        <!-- Tips -->
        <div class="tips-section">
          <h4>💡 Tips for Effective Prompts:</h4>
          <ul>
            <li>Be specific about the desired output format</li>
            <li>Include examples of good responses</li>
            <li>Specify what to avoid or not include</li>
            <li>Keep instructions clear and concise</li>
            <li>Test with different temperature values</li>
          </ul>
        </div>
      </div>
    `;
  }
  
  /**
   * Update the editor textarea with current prompt value
   */
  updateEditorValue() {
    const textarea = document.getElementById('prompt-editor-textarea');
    if (textarea && this.originalPrompt) {
      const promptText = this.originalPrompt.systemRole || 
                        this.originalPrompt.template || 
                        this.originalPrompt.content || 
                        '';
      textarea.value = promptText;
      this.updateCharCount();
    }
  }
  
  /**
   * Attach event handlers to editor elements
   */
  attachEventHandlers() {
    // Character count
    const textarea = document.getElementById('prompt-editor-textarea');
    if (textarea) {
      textarea.addEventListener('input', () => this.updateCharCount());
    }
    
    // Temperature slider
    const tempSlider = document.getElementById('temperature-slider');
    if (tempSlider) {
      tempSlider.addEventListener('input', (e) => {
        document.getElementById('temperature-value').textContent = e.target.value;
      });
    }
    
    // Version rollback buttons
    document.querySelectorAll('[data-action="rollback"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const version = e.target.dataset.version;
        this.rollbackToVersion(version);
      });
    });
    
    // Removed export history functionality
    
    // Old history restore (if exists)
    const restoreBtn = document.getElementById('restore-version');
    if (restoreBtn) {
      restoreBtn.addEventListener('click', () => this.restoreFromHistory());
    }
  }
  
  /**
   * Rollback to a specific version
   */
  rollbackToVersion(version) {
    confirmOverlay('Rollback to Version ' + version + '?', 'This will restore the prompt to version ' + version + '. Continue?', () => {
        try {
          const content = promptVersionControl.rollback(this.currentPromptKey, version);
          const textarea = document.getElementById('prompt-editor-textarea');
          if (textarea) {
            textarea.value = content;
            this.updateCharCount();
            Toast.show(`Rolled back to version ${version}`, 'success');
          }
        } catch (error) {
          Toast.show('Failed to rollback: ' + error.message, 'error');
        }
      }, () => {});
  }
  
  /**
   * Update character count display
   */
  updateCharCount() {
    const textarea = document.getElementById('prompt-editor-textarea');
    const countEl = document.getElementById('char-count');
    if (textarea && countEl) {
      countEl.textContent = textarea.value.length;
      
      // Warn if too long (GPT-4 context limits)
      if (textarea.value.length > 4000) {
        countEl.style.color = 'var(--error-color)';
      } else if (textarea.value.length > 3000) {
        countEl.style.color = 'var(--warning-color)';
      } else {
        countEl.style.color = 'var(--text-secondary)';
      }
    }
  }
  
  /**
   * Save the edited prompt
   */
  async save() {
    const textarea = document.getElementById('prompt-editor-textarea');
    const tempSlider = document.getElementById('temperature-slider');
    const changeTypeInput = document.querySelector('input[name="changeType"]:checked');
    const changeDescInput = document.getElementById('change-description');
    
    if (!textarea) return;
    
    const newPromptText = textarea.value.trim();
    const newTemperature = parseFloat(tempSlider?.value || 0.7);
    const changeType = changeTypeInput?.value || 'patch';
    const changeDescription = changeDescInput?.value || 'Updated prompt';
    
    if (!newPromptText) {
      Toast.show('Prompt cannot be empty', 'error');
      return;
    }
    
    try {
      // Save version to version control
      const newVersion = promptVersionControl.saveVersion(
        this.currentPromptKey,
        newPromptText,
        {
          changeType,
          description: changeDescription,
          author: 'User'
        }
      );
      
      // Update the prompt in PromptService
      const updatedPrompt = {
        ...this.originalPrompt,
        systemRole: newPromptText,
        temperature: newTemperature,
        lastModified: new Date().toISOString(),
        customized: true,
        version: newVersion
      };
      
      // Save to PromptService
      PromptService.updatePrompt(this.currentPromptKey, updatedPrompt);
      
      // Save minimal custom prompt to storage for persistence (avoid quota)
      const customPrompts = StorageService.get('customPrompts', {});
      customPrompts[this.currentPromptKey] = {
        systemRole: updatedPrompt.systemRole,
        temperature: updatedPrompt.temperature,
        customized: true,
        lastModified: updatedPrompt.lastModified
      };
      StorageService.set('customPrompts', customPrompts);
      
      // Update stats
      this.updatePromptStats(this.currentPromptKey);
      
      // Emit event for other components to refresh
      EventBus.emit('prompt:updated', {
        key: this.currentPromptKey,
        prompt: updatedPrompt,
        version: newVersion
      });
      
      Toast.show(`Prompt saved as version ${newVersion}`, 'success');
      if (this._closeFn) try { this._closeFn(); } catch(_) {}
      this.isOpen = false;
      
    } catch (error) {
      console.error('Error saving prompt:', error);
      Toast.show('Failed to save prompt', 'error');
    }
  }
  
  /**
   * Cancel editing
   */
  cancel() {
    if (this.hasChanges()) {
      confirmOverlay('Discard Changes?', 'You have unsaved changes. Are you sure you want to discard them?', () => {
        if (this._closeFn) try { this._closeFn(); } catch(_) {}
        this.isOpen = false;
      }, () => {});
    } else {
      if (this._closeFn) try { this._closeFn(); } catch(_) {}
      this.isOpen = false;
    }
  }
  
  /**
   * Reset to default prompt
   */
  resetToDefault() {
    confirmOverlay('Reset to Default?', 'This will restore the original prompt and discard all customizations. Continue?', () => {
        // Get default prompt from PromptService
        const defaultPrompt = PromptService.getDefaultPrompt(this.currentPromptKey);
        if (defaultPrompt) {
          const textarea = document.getElementById('prompt-editor-textarea');
          const tempSlider = document.getElementById('temperature-slider');
          
          if (textarea) {
            textarea.value = defaultPrompt.systemRole || defaultPrompt.template || '';
            this.updateCharCount();
          }
          
          if (tempSlider) {
            tempSlider.value = defaultPrompt.temperature || 0.7;
            document.getElementById('temperature-value').textContent = tempSlider.value;
          }
          
          Toast.show('Reset to default prompt', 'info');
        }
      }, () => {});
  }
  
  /**
   * Check if there are unsaved changes
   */
  hasChanges() {
    const textarea = document.getElementById('prompt-editor-textarea');
    if (!textarea || !this.originalPrompt) return false;
    
    const originalText = this.originalPrompt.systemRole || 
                        this.originalPrompt.template || 
                        this.originalPrompt.content || 
                        '';
    
    return textarea.value.trim() !== originalText.trim();
  }
  
  /**
   * Save current version to history
   */
  saveToHistory() {
    if (!this.promptHistory[this.currentPromptKey]) {
      this.promptHistory[this.currentPromptKey] = {
        history: [],
        editCount: 0,
        lastModified: null
      };
    }
    
    const history = this.promptHistory[this.currentPromptKey];
    
    // Add current version to history (keep last 5)
    history.history.unshift({
      prompt: this.originalPrompt,
      timestamp: new Date().toISOString(),
      editCount: history.editCount
    });
    
    if (history.history.length > 5) {
      history.history.pop();
    }
    
    StorageService.set('promptHistory', this.promptHistory);
  }
  
  /**
   * Restore from version history
   */
  restoreFromHistory() {
    const select = document.getElementById('history-select');
    const index = parseInt(select?.value);
    
    if (!isNaN(index) && this.promptHistory[this.currentPromptKey]) {
      const history = this.promptHistory[this.currentPromptKey].history[index];
      if (history && history.prompt) {
        const textarea = document.getElementById('prompt-editor-textarea');
        if (textarea) {
          textarea.value = history.prompt.systemRole || 
                          history.prompt.template || 
                          '';
          this.updateCharCount();
          Toast.show('Restored from history', 'success');
        }
      }
    }
  }
  
  /**
   * Get prompt statistics
   */
  getPromptStats(promptKey) {
    if (!this.promptHistory[promptKey]) {
      return {
        editCount: 0,
        lastModified: null,
        history: []
      };
    }
    
    return this.promptHistory[promptKey];
  }
  
  /**
   * Update prompt statistics
   */
  updatePromptStats(promptKey) {
    if (!this.promptHistory[promptKey]) {
      this.promptHistory[promptKey] = {
        history: [],
        editCount: 0,
        lastModified: null
      };
    }
    
    this.promptHistory[promptKey].editCount++;
    this.promptHistory[promptKey].lastModified = new Date().toISOString();
    
    StorageService.set('promptHistory', this.promptHistory);
  }
  
  /**
   * Create quick edit button for tabs
   * @param {string} promptKey - The prompt key
   * @param {string} promptName - Display name
   * @returns {HTMLElement} Button element
   */
  static createEditButton(promptKey, promptName) {
    const button = document.createElement('button');
    button.className = 'btn-primary btn-small prompt-edit-btn';
    button.innerHTML = `
      <span class="btn-icon">⚙️</span>
      <span>Configure</span>
    `;
    button.title = `Edit ${promptName} prompt`;
    button.dataset.promptKey = promptKey;
    button.dataset.promptName = promptName;
    
    // Create editor instance if needed
    if (!window.promptEditor) {
      window.promptEditor = new PromptEditor();
    }
    
    button.addEventListener('click', () => {
      window.promptEditor.open(promptKey, promptName);
    });
    
    return button;
  }
}

// Create singleton instance
export const promptEditor = new PromptEditor();

// Attach to window for global access
if (import.meta.env.DEV) {
  window.promptEditor = promptEditor;
  window.PromptEditor = PromptEditor;
}

// Add styles
const styles = `
<style id="prompt-editor-styles">
.prompt-editor {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Version Info Bar */
.version-info-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border: 1px solid var(--primary-color);
  border-radius: 8px;
}

.version-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.version-label {
  font-size: 0.875rem;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.version-number {
  font-size: 1.25rem;
  font-weight: bold;
  color: var(--primary-color);
}

.version-stats {
  display: flex;
  gap: 1.5rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.version-stats span {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

/* Version History List */
.version-history-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 0.5rem;
}

.version-history-item {
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  transition: all 0.2s;
}

.version-history-item:hover {
  border-color: var(--primary-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.version-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.version-tag {
  padding: 0.25rem 0.5rem;
  background: var(--primary-color);
  color: white;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: bold;
}

.version-date {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.version-item-details {
  display: flex;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
}

.version-author {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.version-diff {
  padding: 0.125rem 0.375rem;
  background: var(--bg-secondary);
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.75rem;
}

/* Change Type Section */
.change-type-section {
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 8px;
}

.change-type-selector {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 1rem 0;
}

.change-type-selector input[type="radio"] {
  margin-right: 0.5rem;
}

.change-type-selector label {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.change-type-selector label:hover {
  background: var(--bg-primary);
}

.change-type-selector strong {
  margin-right: 0.5rem;
}

.change-description {
  margin-top: 1rem;
}

.change-description input {
  width: 100%;
  padding: 0.5rem;
  margin-top: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--bg-primary);
  color: var(--text-primary);
}

.editor-info {
  display: flex;
  gap: 2rem;
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 8px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.info-value {
  font-weight: 500;
  color: var(--text-primary);
}

.editor-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.prompt-textarea {
  width: 100%;
  min-height: 300px;
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.9rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  resize: vertical;
}

.char-count {
  text-align: right;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.variables-section,
.temperature-section,
.history-section,
.tips-section {
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 8px;
}

.variables-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.variable-tag {
  padding: 0.25rem 0.5rem;
  background: var(--primary-light);
  color: var(--primary-color);
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.875rem;
}

.temperature-slider {
  width: 100%;
  margin: 1rem 0;
}

.history-select {
  width: 100%;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--bg-primary);
  color: var(--text-primary);
}

.tips-section h4 {
  margin: 0 0 1rem 0;
  color: var(--text-primary);
}

.tips-section ul {
  margin: 0;
  padding-left: 1.5rem;
  color: var(--text-secondary);
}

.tips-section li {
  margin: 0.5rem 0;
}

.help-text {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0.5rem 0 0 0;
}

/* Prompt Edit Button */
.prompt-edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.prompt-editor-modal .modal-content {
  max-width: 800px;
}

/* Use global .btn-small styles from main stylesheets */
</style>
`;

// Inject styles if not already present
if (!document.getElementById('prompt-editor-styles')) {
  document.head.insertAdjacentHTML('beforeend', styles);
}
